import { computed, ref } from 'vue'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  ECOMMERCE_ITEM_PRICE_TYPE,
  ECOMMERCE_ITEM_TAG,
  type EcommerceItem,
  type EcommerceItemPrice,
  SALE_DISCOUNT_TYPE,
  SPECIAL_ECOMMERCE_ITEM_ID,
} from '@/types/shared/player-config'
import api from '@/services/api'
import WebApp from '@twa-dev/sdk'
import { useAnalytics } from '@/composables/analytics'
import { sleep } from '@/services/helper'
import { useConfig } from '@/composables/game/config'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { player, syncPlayer } from '@/composables/game/player'
import { watchOnce } from '@vueuse/core'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { hideTonPayment } from '@/composables/init'
import { useBatchFastForwardsCooldown } from '@/composables/game/upgrade'

export type PurchaseMetadata = {
  type: 'puzzle'
  step: number
} | undefined

type PurchaseInput = ({
  type: 'item'
  item: EcommerceItem
  priceType?: ECOMMERCE_ITEM_PRICE_TYPE
} | {
  type: 'card',
  upgradeId: string,
  price: EcommerceItemPrice
  priceType?: ECOMMERCE_ITEM_PRICE_TYPE
  itemId: SPECIAL_ECOMMERCE_ITEM_ID.CARD_UPGRADE | SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD
} | {
  type: 'batch-card-fast-forward',
  item: EcommerceItem
  priceType: ECOMMERCE_ITEM_PRICE_TYPE
}) & { metadata?: PurchaseMetadata | undefined }

type PurchaseOutput = { paid: false } | { paid: true, nonce: string }

const starsBalanceConfirmModalConfig = ref<{ amount: number, accepted: boolean }>({ amount: 0, accepted: false })
const showStarsBalanceConfirmModal = computed(() => starsBalanceConfirmModalConfig.value.amount > 0)
const buyingItemId = ref<null | string>(null)

export const useEcommerceItems = () => {
  const { config } = useConfig()
  return {
    ecommerceItems: computed(() => config.value.game.ecommerce.items.filter(item => !item.archivedAt)),
  }
}

export const useEcommerce = () => {
  const { triggerEvent } = useAnalytics()
  const { config } = useConfig()
  const { getStarPrice } = useEcommercePrice()
  const { doTonPurchaseTransaction } = useWalletConnect()

  const playerStarsBalance = computed(() => player.value.ecommerce.starsBalance)

  function openInvoice(invoiceUrl: string): Promise<'pending' | 'failed' | 'cancelled' | 'paid'> {
    return new Promise((resolve) => {
      WebApp.openInvoice(invoiceUrl, resolve)
    })
  }

  function buildItemAnalyticsMetadata(item: EcommerceItem, usdAmount?: number) {
    return {
      item_id: item.id,
      item_name: item.name,
      index: 0,
      price: usdAmount ?? getStarPrice(item)?.usdAmount ?? 0,
      quantity: 1,
    }
  }

  async function purchaseAndGetNonce(input: PurchaseInput): Promise<PurchaseOutput> {
    const item = 'item' in input ? input.item : ('itemId' in input ? config.value.game.ecommerce.items.find(i => i.id === input.itemId) : undefined)
    if (!item) {
      await api.reportError({
        code: 'upgrade_card_not_found',
        metadata: {},
        description: 'cardUpgrade item not found',
      })
      throw Error('cardUpgrade item not found')
    }

    const itemMetadata = buildItemAnalyticsMetadata(item, input.type === 'card' ? input.price.usdAmount : item.prices[0]?.usdAmount)
    const itemPriceType = input.type === 'batch-card-fast-forward' ? input.priceType
      : (input.type === 'item' ?
        (('priceType' in input) ? (input.priceType ?? ECOMMERCE_ITEM_PRICE_TYPE.STAR) : ECOMMERCE_ITEM_PRICE_TYPE.STAR) :
        input.price.type)
    const isStarsPayment = itemPriceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR

    const ecommerceMetadata = {
      currency: 'USD',
      value: itemMetadata.price,
      items: [
        itemMetadata,
      ],
    }
    triggerEvent({
      event: 'begin_checkout',
      metadata: {
        ecommerce: ecommerceMetadata,
      },
    })
    buyingItemId.value = item.id
    try {
      let invoiceUrl = ''
      let nonce = ''
      let response: {
        isPaid: false
        invoiceUrl: string
        nonce: string
        amount: number
      } | {
        isPaid: true
      } | null = null
      if (input.type === 'item') {
        response = await api.getEcommerceInvoiceUrl({
          itemId: item.id,
          priceType: itemPriceType,
          metadata: input.metadata,
        })
      } else if (input.type === 'card') {
        response = await api.getCardInvoiceUrl({
          upgradeId: input.upgradeId,
          itemId: input.itemId,
          priceType: itemPriceType,
        })
      } else {
        response = await api.getBatchCardFastForwardInvoiceUrl({
          priceType: itemPriceType,
        })
      }
      if (response) {
        if (response.isPaid) {
          return { paid: true, nonce: '' }
        }
        invoiceUrl = response.invoiceUrl
        nonce = response.nonce
        if (itemPriceType === ECOMMERCE_ITEM_PRICE_TYPE.TON) {
          await doTonPurchaseTransaction({
            tonAmount: response.amount,
            nonce: nonce,
          })
        }
      }

      let result = ''
      if (isStarsPayment) {
        result = await openInvoice(invoiceUrl) as string
        if (result !== 'paid') {
          return { paid: false }
        }
      }
      for (let i = 0; i < (isStarsPayment ? 15 : 60); i++) {
        await sleep(isStarsPayment ? 1000 : 3000)
        const { status } = await api.getPurchaseStatus({ nonce })
        if (status === 'completed') {
          result = 'paid'
          break
        }
      }

      if (result === '') {
        throw new Error('purchase timed out')
      }

      if (result === 'paid' && itemPriceType !== ECOMMERCE_ITEM_PRICE_TYPE.TON) {
        triggerEvent({
          event: 'purchase',
          metadata: {
            value: itemMetadata.price,
            product_item_id: item.trackingId ?? item.id,
            product_item_name: item.name,
            discountType: config.value.game.ecommerce.sales?.discount?.type ?? undefined,
            discountAmount: (config.value.game.ecommerce.sales?.discount as {
              type: SALE_DISCOUNT_TYPE.DISCOUNT | SALE_DISCOUNT_TYPE.CASHBACK,
              amount: number
            })?.amount ?? undefined,
            ecommerce: {
              ...ecommerceMetadata,
              transaction_id: nonce,
            },
          },
        })
      }

      return { paid: result === 'paid', nonce }

    } catch (e) {
      await api.reportError({
        code: 'failedToPurchase',
        description: (e as Error).message,
        metadata: item,
      })
      return { paid: false }
    } finally {
      buyingItemId.value = null
    }
  }

  async function confirmAndPurchase(input: PurchaseInput): Promise<PurchaseOutput> {
    const item = 'item' in input ? input.item : ('itemId' in input ? config.value.game.ecommerce.items.find(i => i.id === input.itemId) : undefined)
    if (!item) {
      await api.reportError({
        code: 'upgrade_card_not_found',
        metadata: {},
        description: 'cardUpgrade item not found',
      })
      throw Error('cardUpgrade item not found')
    }

    let price: EcommerceItemPrice | undefined
    if (input.type === 'batch-card-fast-forward') {
      const { discountedTotalPrices } = useBatchFastForwardsCooldown()
      price = {
        amount: input.priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR ? discountedTotalPrices.value.starAmount : discountedTotalPrices.value.tonAmount,
        type: input.priceType,
        usdAmount: discountedTotalPrices.value.starUsdAmount,
        originalUsdAmount: discountedTotalPrices.value.originalUsdAmount,
      }
    } else {
      price = input.type === 'item' ? item.prices.find(p => p.type === (input.priceType ?? ECOMMERCE_ITEM_PRICE_TYPE.STAR)) : input.price
    }
    if (!price) {
      await api.reportError({
        code: 'price_type_not_found',
        metadata: { input },
        description: 'missing price type',
      })
      throw Error('missing price type')
    }

    const isStarsPayment = input.priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR
    const hasStarBenefit = item.benefits.some(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.STAR)
    const isFree = price.amount === 0

    if (hasStarBenefit || isFree || !isStarsPayment || playerStarsBalance.value < price.amount) {
      return await purchaseAndGetNonce(input)
    }

    const isConfirmed = await showConfirmModal(price.amount)
    if (!isConfirmed) return { paid: false }

    const result = await purchaseAndGetNonce(input)
    starsBalanceConfirmModalConfig.value = {
      amount: 0,
      accepted: false,
    }
    return result
  }

  function showConfirmModal(amount: number) {
    return new Promise((resolve) => {
      starsBalanceConfirmModalConfig.value = {
        amount,
        accepted: false,
      }

      watchOnce(starsBalanceConfirmModalConfig, (starsBalanceConfirmModalConfig) => {
        resolve(starsBalanceConfirmModalConfig.accepted)
      }, { deep: true })
    })
  }

  return {
    buyingItemId: buyingItemId,
    purchase: confirmAndPurchase,
    buildItemAnalyticsMetadata: buildItemAnalyticsMetadata,
    showStarsBalanceConfirmModal,
    starsBalanceConfirmModalConfig,
  }
}

export const useLimitedTimeOffers = () => {
  const { ecommerceItems } = useEcommerceItems()
  const starterPackConfig = computed(() => ecommerceItems.value.find(i => !i.archivedAt && i.tags.includes(ECOMMERCE_ITEM_TAG.STARTER)))
  const afterStarterPackConfig = computed(() => ecommerceItems.value.find(i => !i.archivedAt && i.tags.includes(ECOMMERCE_ITEM_TAG.AFTER_STARTER)))
  const afterStarterPackDuration = computed(() => afterStarterPackConfig.value?.limits.time?.duration ?? 0)
  return {
    starterPack: starterPackConfig,
    afterStarterPack: afterStarterPackConfig,
    afterStarterPackDuration,
  }
}

export const useEcommercePrice = () => {

  const getStarPrice = (item: EcommerceItem) => item.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR) as EcommerceItemPrice
  const getTonPrice = (item: EcommerceItem) => item.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.TON) ?? null

  return {
    getStarPrice,
    getTonPrice,
  }
}

export const useEcommerceItemDetailsPurchase = (item: EcommerceItem) => {
  const { getStarPrice, getTonPrice } = useEcommercePrice()
  const { purchase, buyingItemId } = useEcommerce()
  const { rawAddress } = useWalletConnect()
  const { open } = useTonConnectModal()

  const starPrice = computed(() => getStarPrice(item))
  const tonPrice = computed(() => getTonPrice(item))
  const selectedPriceType = ref<ECOMMERCE_ITEM_PRICE_TYPE | null>(null)

  const isBuying = computed(() => buyingItemId.value === item.id)
  const isBuyingWithStars = computed(() => isBuying.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.STAR)
  const isBuyingWithTon = computed(() => isBuying.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.TON)

  const showTonPurchase = computed(() => !hideTonPayment.value && !!tonPrice.value && tonPrice.value.amount > 0)

  const canHaveSale = computed(() => item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT))

  async function onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE, metadata?: PurchaseMetadata) {
    if (isBuying.value) return false
    selectedPriceType.value = priceType

    try {
      if (priceType === ECOMMERCE_ITEM_PRICE_TYPE.TON && !rawAddress.value) {
        open()

        // Wrap the watchOnce in a Promise
        const waitForAddress = new Promise<boolean>((resolve) => {
          watchOnce(rawAddress, async () => {
            if (!rawAddress.value) return resolve(false)

            const paid = await doPurchase(priceType, metadata)
            selectedPriceType.value = null
            resolve(paid)
          })
        })

        return await waitForAddress
      }

      const paid = await doPurchase(priceType, metadata)
      selectedPriceType.value = null
      return paid
    } catch (e) {
      selectedPriceType.value = null
      return false
    }
  }

  async function doPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE, metadata?: PurchaseMetadata) {
    const { paid } = await purchase({
      type: item.id === SPECIAL_ECOMMERCE_ITEM_ID.CARD_FAST_FORWARD_ALL ? 'batch-card-fast-forward' : 'item',
      item: item,
      priceType: priceType,
      metadata: metadata,
    })
    if (paid) {
      await syncPlayer()
    }
    return paid
  }

  return {
    starPrice,
    tonPrice,
    selectedPriceType,
    isBuying,
    isBuyingWithStars,
    isBuyingWithTon,
    showTonPurchase,
    canHaveSale,
    onPurchase,
  }
}
